import { toast } from '@/components/ui/use-toast.jsx';
import { TABS } from '@/constants';
import {
	MarketStatus,
	TradePosition,
	TradeStatus,
	UserRole,
	MARKET_BULK_CREATED,
	MARKET_BULK_UPDATED,
	MARKET_CREATED,
	MARKET_DELETED,
	MARKET_UPDATED,
	TRADE_CREATED,
} from '@sportspredict/shared';
import { Notification } from '../../api';
import { format, isSameMonth, isSameYear } from 'date-fns';
import { toNum } from '../common/number.util';
import { downloadCSV } from '../common/download.util';

export const getEventParticipants = (lobbies: any[]): string[] => {
	const participants = new Set<string>();
	lobbies?.forEach((lobby) => {
		if (lobby?.lobby_users) {
			lobby.lobby_users.forEach((lobby_user: any) =>
				participants.add(lobby_user?.user_email ?? lobby_user?.user?.email)
			);
		}
	});

	return Array.from(participants).filter(
		(email): email is string => typeof email === 'string' && email.trim().length > 0
	);
};

export const filterBotTrades = (trades: any) => {
	const res = [];
	let botSegment = [];

	const isInflection = (a: any, b: any, c: any) =>
		(b.entry_price - a.entry_price) * (c.entry_price - b.entry_price) < 0;

	const processSegment = (segment: any) => {
		if (segment.length === 0) return;
		res.push(segment[0]);
		for (let i = 1; i < segment.length - 1; i++) {
			if (isInflection(segment[i - 1], segment[i], segment[i + 1])) {
				res.push(segment[i]);
			}
		}
		if (segment.length > 1) res.push(segment.at(-1));
	};

	for (const t of trades) {
		if (t.user_role === UserRole.Bot) botSegment.push(t);
		else {
			processSegment(botSegment);
			botSegment = [];
			res.push(t);
		}
	}
	processSegment(botSegment);
	return res;
};

export const getTradeHistoryCardColorsAndStatus = (market: any, trade: any) => {
	if (!market || !trade) return;

	const isLong = trade.position === TradePosition.Long;
	const isShort = trade.position === TradePosition.Short;
	const isShortWin = market?.current_value === 0;
	const isLongWin = market?.current_value === 100;
	const isSettled = market?.status === MarketStatus.Settled;

	if (isSettled && ((isLong && isLongWin) || (isShort && isShortWin))) {
		return { status: TradeStatus.Win, colors: { stroke: '#2667FF4D', gradient: '#1B744DBF' } };
	}

	if (isSettled) {
		return { status: TradeStatus.Lose, colors: { stroke: '#2667FF4D', gradient: '#611818BF' } };
	}

	return {
		status: TradeStatus.Pending,
		colors: isLong
			? { stroke: '#2667FF4D', gradient: '#2667FF4D' }
			: { stroke: '#2667FF4D', gradient: '#2667FF4D' },
	};
};

export const validateLobbyForm = (formData: any, setErrors: any) => {
	let hasError = false;
	const newErrors = [];

	if (!formData.name?.trim()) {
		newErrors.push('name');
		hasError = true;
	}

	if (!formData.info?.trim()) {
		newErrors.push('info');
		hasError = true;
	}

	//if (!formData.prize?.trim()) {
	//	newErrors.push('prize');
	//	hasError = true;
	//}

	if (formData.is_private && !formData.access_password?.trim()) {
		newErrors.push('access_password');
		hasError = true;
	}

	const details = formData.details || [];

	details.forEach((d: any, i: any) => {
		const hasAnyValue = d.header?.trim() || d.content?.trim();
		const isIncomplete = !d.header?.trim() || !d.content?.trim();

		if (hasAnyValue && isIncomplete) {
			newErrors.push(`details[${i}]`);
			hasError = true;
		}
	});

	if (hasError) {
		setErrors(newErrors);
		toast({
			variant: 'destructive',
			title: 'Error',
			description: 'Please fill in all required fields',
		});
	}

	return !hasError;
};

export const tabLobbyFormConfig = (isEdit: any) => {
	const tabs = [
		{
			key: 'settings',
			name: 'Lobby Details',
			header: 'Lobby Settings',
			details: `${isEdit ? 'Update' : 'Create'} lobby details and settings.`,
			fields: ['name', 'access_password'],
		},
		{
			key: 'description',
			name: 'Description',
			header: `${isEdit ? 'Edit' : 'Create'} Lobby Description`,
			details: 'Description appears when players click on the Lobby Info button. ',
			fields: ['info', 'prize', 'details'],
		},
	];

	if (isEdit) {
		tabs.push({
			key: 'share',
			name: 'Share',
			header: ``,
			details: '',
			fields: ['link'],
		});
	}

	return tabs;
};

export const formatBulletPoints = (value: string): string => value.replace(/(^|\n)-\s/g, '$1• ');

export const clearLobbyFormError = (details: any, errorFields: any, setErrorFields: any) => {
	const validDetailIndices = details
		.map((d: any, i: any) => ({ i, isValid: d.header?.trim() && d.content?.trim() }))
		.filter(({ i, isValid }: any) => isValid && errorFields.includes(`details[${i}]`))
		.map(({ i }: any) => i);

	if (validDetailIndices.length > 0) {
		setErrorFields((prev: any) =>
			prev.filter((f: any) => !validDetailIndices.includes(+f.match(/^details\[(\d+)\]$/)?.[1]))
		);
	}
};

export const exportParticipantsToCSV = (lobby: any) => {
	try {
		const participants = lobby.lobby_users || [];

		const headers = ['Email', 'Status'];

		const rows = [
			headers,
			...participants.map(({ user_email, is_hide }: any) => [
				user_email,
				is_hide ? 'Hidden' : 'Visible',
			]),
		];

		const safeLobbyName = lobby.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
		const date = format(new Date(), 'yyyy-MM-dd');
		const filename = `${safeLobbyName}_participants_${date}.csv`;

		downloadCSV(rows, filename);

		toast({
			title: 'Success',
			description: `Exported ${participants.length} participants to CSV`,
		});
	} catch (error) {
		toast({
			variant: 'destructive',
			title: 'Error',
			description: 'Failed to export participants',
		});
		console.error(error);
		throw new Error('Error resetting user data');
	}
};

export const getFormattedDateRange = (start: any, end: any) => {
	if (!start || !end) return '';

	const startDate = new Date(start);
	const endDate = new Date(end);

	const sameMonth = isSameMonth(startDate, endDate);
	const sameYear = isSameYear(startDate, endDate);

	if (sameMonth && sameYear) {
		// Example: 20 - 23 JAN 2026
		return `${format(startDate, 'd')} - ${format(endDate, 'd MMM yyyy').toUpperCase()}`;
	} else if (!sameMonth && sameYear) {
		// Example: 20 DEC - 23 JAN 2026
		return `${format(startDate, 'd MMM').toUpperCase()} - ${format(endDate, 'd MMM yyyy').toUpperCase()}`;
	} else {
		// Example: 20 DEC 2025 - 23 JAN 2026
		return `${format(startDate, 'd MMM yyyy').toUpperCase()} - ${format(endDate, 'd MMM yyyy').toUpperCase()}`;
	}
};

export async function sendNotificationInline(email: any, title: any, message: any) {
	if (!email || !title || !message) return;
	try {
		await Notification.create({
			title,
			message,
			type: 'event',
			is_global: false,
			target_users: [email],
			link: '',
			expire_date: null,
			status: 'active',
			is_active: true,
		});
	} catch (err) {
		console.error('Notification error:', err);
	}
}

export function notifyOnce(notifiedRef: any, key: any, email: any, title: any, message: any) {
	if (!email || notifiedRef.current.has(key)) return;
	notifiedRef.current.add(key);
	sendNotificationInline(email, title, message);
}

export const getSuffix = (rank: any) => {
	if (rank == null) return '';
	const lastDigit = rank % 10;
	const lastTwoDigits = rank % 100;

	if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
		return 'th';
	}

	switch (lastDigit) {
		case 1:
			return 'st';
		case 2:
			return 'nd';
		case 3:
			return 'rd';
		default:
			return 'th';
	}
};

export function sortLobbiesByPriorityStable(rawLobbies: any) {
	if (!Array.isArray(rawLobbies)) return [];

	const anyHasPriority = rawLobbies.some((l: any) => Number.isFinite(l?.priority));
	if (!anyHasPriority) return rawLobbies;

	return rawLobbies
		.map((lobby: any, index: any) => ({ lobby, index }))
		.sort((a: any, b: any) => {
			const aKey = Number.isFinite(a.lobby?.priority) ? a.lobby.priority : Number.POSITIVE_INFINITY;
			const bKey = Number.isFinite(b.lobby?.priority) ? b.lobby.priority : Number.POSITIVE_INFINITY;
			if (aKey !== bKey) return aKey - bKey;
			return a.index - b.index;
		})
		.map((x: any) => x.lobby);
}

export const isChangeMarketStatus = (oldMarket: any, newMarket: any) => {
	if (!oldMarket) return false;

	const oldStatus = oldMarket.status;
	const newStatus = newMarket.status;

	if (oldStatus === newStatus) return false;

	if (oldStatus === MarketStatus.Settled && newStatus !== MarketStatus.Settled) {
		return true;
	}

	if (oldStatus === MarketStatus.Queue && newStatus !== MarketStatus.Queue) {
		return true;
	}

	if (newStatus === MarketStatus.Settled && oldStatus !== MarketStatus.Settled) {
		return true;
	}

	return false;
};

export const handleLeaderboardSocketUpdate = (
	sockData: any,
	activeTab: any,
	isOverallLeaderboard: any,
	selectedMatches: any,
	isOddsLocked: any,
	markets: any,
	scheduleRefetchLeaderboard: any,
	scheduleRefetchUserLeaderboardRank: any,
	setShouldRefetchLeaderboard: any
) => {
	const { tag } = sockData;
	const isRelevantMatch = (matchId: any) =>
		isOverallLeaderboard || selectedMatches.includes(matchId ?? '');

	const triggerRefetch = (isMatchRelevant: any) => {
		scheduleRefetchUserLeaderboardRank?.();

		if (!isMatchRelevant) return;

		if (activeTab === TABS.LEADERBOARD) {
			scheduleRefetchLeaderboard();
		} else {
			setShouldRefetchLeaderboard?.(true);
		}
	};

	if (tag === TRADE_CREATED || tag === MARKET_DELETED) {
		const relevant = isRelevantMatch(sockData?.market?.match_id);
		triggerRefetch(relevant);
		return;
	}

	if (tag === MARKET_CREATED && isOddsLocked) {
		const relevant = isRelevantMatch(sockData?.market?.match_id);
		triggerRefetch(relevant);
		return;
	}

	if (tag === MARKET_BULK_CREATED && isOddsLocked) {
		const relevant = sockData?.markets?.some((m: any) => isRelevantMatch(m.match_id));
		triggerRefetch(relevant);
		return;
	}

	if (tag === MARKET_UPDATED) {
		const newMarket = sockData.market;
		const oldMarket = markets.find((m: any) => m.id === newMarket.id);
		const relevant =
			isRelevantMatch(newMarket?.match_id) && isChangeMarketStatus(oldMarket, newMarket);
		triggerRefetch(relevant);
		return;
	}

	if (tag === MARKET_BULK_UPDATED) {
		const updated = sockData.markets || [];

		const relevant = updated.some((m: any) => {
			const oldMarket = markets.find((ms: any) => ms.id === m.id);
			return isRelevantMatch(m.match_id) && isChangeMarketStatus(oldMarket, m);
		});

		triggerRefetch(relevant);
		return;
	}
};

export function calculateWinRate(markets: any, userTrades: any) {
	if (!markets?.length || !userTrades?.length) return null;

	let wins = 0;
	let settled = 0;

	markets.forEach((market: any) => {
		if (market.status !== MarketStatus.Settled) return;
		const userTrade = userTrades.find((t: any) => t.market_id === market.id);
		if (!userTrade) return;

		settled++;

		if (
			(market.current_value === 100 && userTrade.position === TradePosition.Long) ||
			(market.current_value === 0 && userTrade.position === TradePosition.Short)
		) {
			wins++;
		}
	});

	if (settled === 0) return null;

	return Math.round((wins / settled) * 100);
}

export function formatWinRate(winRate: any) {
	if (winRate === null || winRate === undefined) return '-';
	return `${winRate}%`;
}

export const expandSelectionToGroup = (tab: any, allSlates: any) => {
	if (!tab?.selected?.slates?.length || !allSlates?.length) return;

	const selectedSlateIds = tab.selected.slates;
	const groupNamesToExpand = new Set();

	for (const slateId of selectedSlateIds) {
		const slate = allSlates.find((s: any) => s.id === slateId);
		if (slate?.group_name) {
			groupNamesToExpand.add(slate.group_name);
		}
	}

	if (groupNamesToExpand.size === 0) return;

	const expandedSlateIds = new Set(selectedSlateIds);
	const expandedMatches = [];

	for (const slate of allSlates) {
		if (slate.group_name && groupNamesToExpand.has(slate.group_name)) {
			expandedSlateIds.add(slate.id);
			if (slate.matches) {
				expandedMatches.push(...slate.matches.map((m: any) => m.id));
			}
		}
	}
	tab.selected.slates = [...expandedSlateIds];
	tab.selected.matches = expandedMatches;
};

export const getGlobalTradeCount = (u: any) =>
	Number(u?.total_trades ?? u?.trade_count ?? u?.stats?.total_trades ?? 0);

export const getSlateMarketsCount = ({
	markets,
	currentSlateMatchIds,
	userTrades = [],
	isRemaining = false,
}: {
	markets: any[];
	currentSlateMatchIds: Set<any>;
	userTrades?: any[];
	isRemaining?: boolean;
}) => {
	if (!markets?.length || !currentSlateMatchIds || currentSlateMatchIds.size === 0) return 0;

	const tradedMarketIds = isRemaining ? new Set(userTrades.map((t: any) => t.market_id)) : null;

	return markets.reduce((count: number, m: any) => {
		if (!m?.match_id) return count;
		if (!currentSlateMatchIds.has(m.match_id)) return count;

		if (isRemaining) {
			if (m.status !== MarketStatus.Open) return count;
			if (tradedMarketIds?.has(m.id)) return count;
		}

		return count + 1;
	}, 0);
};

export const getEliminationInfo = ({
	userTrades,
	markets,
}: {
	userTrades: any[];
	markets: any[];
}) => {
	const settledTrades = userTrades
		.filter((t: any) => t.status === TradeStatus.Lose || TradeStatus.Win)
		.sort(
			(a: any, b: any) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime()
		);

	for (let i = 0; i < settledTrades.length; i++) {
		const trade = settledTrades[i];

		const market = markets.find(
			(m: any) => m.id === trade.market_id || m.market_id === trade.market_id
		);

		const userAnswer =
			trade.position === TradePosition.Long
				? 'YES'
				: trade.position === TradePosition.Short
					? 'NO'
					: null;

		const correctAnswer = Number(market?.current_value) >= 50 ? 'YES' : 'NO';

		if (userAnswer !== correctAnswer) {
			return {
				isEliminated: true,
				eliminatedAtRound: i + 1,
				losingTrade: {
					tradeId: trade.id,
					marketId: market?.id,
					userAnswer,
					correctAnswer,
				},
			};
		}
	}

	return {
		isEliminated: false,
		round: settledTrades?.length,
		eliminatedAtRound: null,
		losingTrade: null,
	};
};

export const calculateRoundInfo = (list: any[], isSecondChance: any) => {
	const filtered = list.filter((m: any) => !!m.is_second_chance === !!isSecondChance);
	const settled = filtered.filter(
		(m: any) => (m.status ?? m.market?.status) === MarketStatus.Settled
	);
	const highestSettledPriority = settled.reduce((acc: number, m: any) => {
		const p = toNum(isSecondChance ? m.second_chance_priority : (m.priority ?? m.market?.priority));
		return Math.max(acc, p);
	}, 0);

	const currentRound = highestSettledPriority + 1;
	const currentMarket =
		filtered.find(
			(m: any) =>
				toNum(isSecondChance ? m.second_chance_priority : (m.priority ?? m.market?.priority)) ===
				currentRound
		) ?? null;
	const marketEntity = currentMarket?.market ?? currentMarket;

	return {
		currentRound,
		currentMarketId: marketEntity?.id ?? null,
		currentMarketStatus: marketEntity?.status ?? null,
	};
};

type SocketMarketScope = {
	lobbyId: string;
	visibleMatchIds: ReadonlySet<string>;
};

export const updateMarkets = (prevMarkets: any[], sockData: any, scope?: SocketMarketScope) => {
	let updated = [...(prevMarkets ?? [])];

	const normId = (v: any) => (v == null ? null : String(v));
	const marketId = normId(sockData.marketId || sockData.market?.id);
	const isVisible = (market: any) => {
		if (!scope) return true;

		const belongsToLobby = Array.isArray(market?.lobby_ids)
			? market.lobby_ids.some((id: any) => normId(id) === normId(scope.lobbyId))
			: false;
		if (!belongsToLobby) return false;

		const matchId = normId(market?.match_id);
		return !matchId || scope.visibleMatchIds.has(matchId);
	};

	const findIndex = (id: any) => updated.findIndex((m: any) => normId(m.id) === id);
	const removeAt = (index: number) => {
		if (index !== -1) updated.splice(index, 1);
	};

	const toTime = (v: any) => {
		const t = v ? Date.parse(v) : NaN;
		return Number.isFinite(t) ? t : null;
	};

	switch (sockData.tag) {
		case TRADE_CREATED: {
			if (!marketId || !sockData.market) break;
			const i = findIndex(marketId);
			const candidate = i === -1 ? sockData.market : { ...updated[i], ...sockData.market };
			if (!isVisible(candidate)) {
				removeAt(i);
				break;
			}
			if (i !== -1) updated[i] = candidate;
			else updated.push(candidate);
			break;
		}

		case MARKET_UPDATED: {
			if (!marketId || !sockData.market) break;

			const i = findIndex(marketId);
			if (i === -1) break;
			const candidate = { ...updated[i], ...sockData.market };
			if (!isVisible(candidate)) {
				removeAt(i);
				break;
			}

			const prevTime = toTime(updated[i]?.updated_date);
			const incomingTime = toTime(sockData.market?.updated_date);

			if (incomingTime != null && prevTime != null) {
				if (incomingTime >= prevTime) {
					updated[i] = candidate;
				}
			} else {
				updated[i] = candidate;
			}

			break;
		}

		case MARKET_BULK_UPDATED:
		case MARKET_BULK_CREATED: {
			(sockData.markets ?? []).forEach((newMarket: any) => {
				const id = normId(newMarket?.id);
				if (!id) return;

				const i = findIndex(id);
				const candidate = i === -1 ? newMarket : { ...updated[i], ...newMarket };
				if (!isVisible(candidate)) {
					removeAt(i);
					return;
				}
				if (i !== -1) {
					updated[i] = candidate;
				} else {
					updated.push(candidate);
				}
			});
			break;
		}

		case MARKET_DELETED: {
			if (!marketId) break;
			updated = updated.filter((m: any) => normId(m.id) !== marketId);
			break;
		}

		case MARKET_CREATED: {
			if (!marketId || !sockData.market) break;
			const i = findIndex(marketId);
			const candidate = i === -1 ? sockData.market : { ...updated[i], ...sockData.market };
			if (!isVisible(candidate)) {
				removeAt(i);
				break;
			}
			if (i === -1) {
				updated.push(sockData.market);
			}
			break;
		}
	}

	return updated;
};
